import Button from "@/components/Button"
import Dialog from "@/components/Dialog"
import InputErrorMessage from "@/components/InputErrorMessage"
import { TextInput } from "@/components/InputFields"
import InputLabel from "@/components/InputLabel"
import Spinner from "@/components/Spinner"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { z } from "zod"

type CSFlagFormData = {
  reason: string
}

type CSFlagDialogProps = {
  isOpen: boolean
  onClose: () => void
  csFlag: boolean
  currentReason?: string
  onSave: (reason: string) => Promise<void>
  onRemove: () => Promise<void>
  isSaving: boolean
  isRemoving: boolean
}

const formSchema = z.object({
  reason: z
    .string()
    .trim()
    .nonempty("Reason is a required field")
    .max(1000, "Reason must be less than 1000 characters"),
})

const CSFlagDialog = ({
  isOpen,
  onClose,
  csFlag,
  currentReason,
  onSave,
  onRemove,
  isSaving,
  isRemoving,
}: CSFlagDialogProps) => {
  const { register, handleSubmit, formState, reset } = useForm<CSFlagFormData>({
    mode: "onChange",
    defaultValues: {
      reason: currentReason ?? "",
    },
    resolver: zodResolver(formSchema),
  })
  const { errors } = formState

  const close = () => {
    onClose()
    reset()
  }

  const onSubmit = (formData: CSFlagFormData) => {
    onSave(formData.reason.trim())
  }

  return (
    <Dialog
      className="max-w-lg"
      title={csFlag ? "Edit CS Flag" : "Add CS Flag"}
      isOpen={isOpen}
      onClose={close}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-3"
        autoComplete="off"
      >
        <div className="flex flex-col gap-1">
          <InputLabel htmlFor="reason" className="ml-1">
            Reason
          </InputLabel>
          <TextInput
            id="reason"
            placeholder="Enter reason for flag"
            isError={!!errors?.reason?.message}
            {...register("reason")}
          />
          <InputErrorMessage
            message={errors?.reason?.message}
            className="ml-1"
          />
        </div>

        <div className="mt-4 flex items-center justify-end gap-4">
          {csFlag && (
            <Button
              type="button"
              variant="danger"
              onClick={onRemove}
              disabled={isRemoving || isSaving}
              className="flex items-center gap-3 px-6"
            >
              {isRemoving && <Spinner className="h-4 w-4" />}
              Remove
            </Button>
          )}
          <Button
            className="flex items-center gap-3 px-6"
            type="submit"
            variant="primary"
            disabled={isSaving || isRemoving}
          >
            {isSaving && <Spinner className="h-4 w-4" />}
            {csFlag ? "Save" : "Add"}
          </Button>
        </div>
      </form>
    </Dialog>
  )
}

export default CSFlagDialog
